/**
 * Minicart - Dynamic Cart Drawer
 * Handles all cart operations via Shopify Cart API
 * Includes contextual Duo -> Topper upsell.
 */

class Minicart {
  constructor() {
    this.drawer = document.querySelector('[data-minicart]');
    if (!this.drawer) return;

    this.overlay = this.drawer.querySelector('[data-minicart-overlay]');
    this.content = this.drawer.querySelector('[data-minicart-content]');
    this.itemsContainer = this.drawer.querySelector('[data-minicart-items]');
    this.emptyState = this.drawer.querySelector('[data-minicart-empty]');
    this.footer = this.drawer.querySelector('[data-minicart-footer]');
    this.totalPrice = this.drawer.querySelector('[data-minicart-total]');
    this.reviewContainer = this.drawer.querySelector('[data-minicart-review]');
    this.cartCountElements = document.querySelectorAll('[data-cart-count]');

    this.isOpen = false;
    this.isUpdating = false;

    this.duoProductId = 15387628699974;
    this.topperProductId = 15387628732742;
    this.topperVariants = {
      '70-140/180': { id: 55968005095750, size: '70x180', price: 19900 },
      '70-140/200': { id: 55968005128518, size: '70x200', price: 19900 },
      '80-160/180': { id: 56959948161350, size: '80x180', price: 22000 },
      '80-160/200': { id: 56959945605446, size: '80x200', price: 22000 },
      '90-180/180': { id: 55968005161286, size: '90x180', price: 25000 },
      '90-180/200': { id: 55968005194054, size: '90x200', price: 25000 }
    };

    this.injectUpsellStyles();
    this.init();
  }

  init() {
    document.querySelectorAll('[data-minicart-open]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-minicart-close]').forEach(trigger => {
      trigger.addEventListener('click', () => this.close());
    });

    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.drawer.addEventListener('click', (e) => this.handleItemClick(e));
    document.addEventListener('cart:updated', () => this.refresh());
    this.refresh();
  }

  injectUpsellStyles() {
    if (document.getElementById('duo-topper-minicart-styles')) return;
    const style = document.createElement('style');
    style.id = 'duo-topper-minicart-styles';
    style.textContent = `
      .minicart__upsell{margin:12px 16px 4px;padding:14px;border:1px solid #ddd7cd;background:#faf8f5;border-radius:12px}
      .minicart__upsell-label{font:700 10px/1.2 Inter,sans-serif;letter-spacing:.08em;text-transform:uppercase;color:#897455;margin-bottom:5px}
      .minicart__upsell-title{font:600 14px/1.3 Inter,sans-serif;color:#1a1a1a;margin:0 0 5px}
      .minicart__upsell-copy{font:400 12px/1.45 Inter,sans-serif;color:#555;margin:0 0 10px}
      .minicart__upsell-row{display:flex;align-items:center;gap:10px}
      .minicart__upsell-price{font:700 14px/1 Inter,sans-serif;color:#1a1a1a;white-space:nowrap}
      .minicart__upsell-btn{flex:1;border:0;background:#897455;color:#fff;min-height:40px;padding:10px 12px;font:600 12px/1 Inter,sans-serif;text-transform:uppercase;letter-spacing:.04em;cursor:pointer}
      .minicart__upsell-btn[disabled]{opacity:.65;cursor:wait}
      .minicart__upsell-link{display:inline-block;margin-top:8px;font:500 11px/1.3 Inter,sans-serif;color:#1a1a1a;text-decoration:underline;text-underline-offset:2px}
      .minicart__upsell-status{margin-top:7px;font:500 11px/1.3 Inter,sans-serif;color:#555;min-height:14px}
    `;
    document.head.appendChild(style);
  }

  open() {
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    document.body.classList.add('minicart-open');
    this.refresh();
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('minicart-open');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  async refresh() {
    try {
      const cart = await this.fetchCart();
      this.render(cart);
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error('Minicart refresh error:', error);
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return response.json();
  }

  render(cart) {
    if (cart.item_count === 0) {
      this.showEmptyState();
      return;
    }

    this.hideEmptyState();
    this.renderItems(cart.items);
    this.renderUpsell(cart.items);
    this.renderTotal(cart.total_price);
    this.renderReview();
  }

  showEmptyState() {
    if (this.emptyState) this.emptyState.style.display = 'flex';
    if (this.itemsContainer) this.itemsContainer.innerHTML = '';
    this.removeUpsell();
    if (this.reviewContainer) this.reviewContainer.innerHTML = '';
    if (this.footer) this.footer.style.display = 'none';
  }

  hideEmptyState() {
    if (this.emptyState) this.emptyState.style.display = 'none';
    if (this.footer) this.footer.style.display = 'block';
  }

  renderItems(items) {
    if (!this.itemsContainer) return;
    this.itemsContainer.innerHTML = items.map(item => this.renderItem(item)).join('');
  }

  renderItem(item) {
    const variantTitle = this.getVariantTitle(item);
    const imageUrl = item.featured_image?.url
      ? this.getSizedImageUrl(item.featured_image.url, '200x')
      : '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

    return `
      <li class="minicart__item" data-item-key="${item.key}">
        <div class="minicart__item-image">
          <a href="${item.url}"><img src="${imageUrl}" alt="${this.escapeHtml(item.title)}" loading="lazy"></a>
        </div>
        <div class="minicart__item-details">
          <h4 class="minicart__item-title"><a href="${item.url}">${this.escapeHtml(item.product_title)}</a></h4>
          ${variantTitle ? `<p class="minicart__item-variant">Wariant: ${this.escapeHtml(variantTitle)}</p>` : ''}
          <div class="minicart__item-quantity">
            <button type="button" class="minicart__quantity-btn" data-action="decrease" data-key="${item.key}" ${item.quantity <= 1 ? 'disabled' : ''} aria-label="Zmniejsz ilość">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <span class="minicart__quantity-value">${item.quantity}</span>
            <button type="button" class="minicart__quantity-btn" data-action="increase" data-key="${item.key}" aria-label="Zwiększ ilość">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
          </div>
        </div>
        <div class="minicart__item-actions">
          <button type="button" class="minicart__item-remove" data-action="remove" data-key="${item.key}" aria-label="Usuń produkt">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/></svg>
          </button>
          <span class="minicart__item-price">${this.formatMoney(item.final_line_price)}</span>
        </div>
        <div class="minicart__loading"><div class="minicart__spinner"></div></div>
      </li>
    `;
  }

  renderUpsell(items) {
    this.removeUpsell();
    if (!this.content || !this.itemsContainer) return;

    const duoItem = items.find(item => Number(item.product_id) === this.duoProductId);
    const topperAlreadyInCart = items.some(item => Number(item.product_id) === this.topperProductId);
    if (!duoItem || topperAlreadyInCart) return;

    const duoSize = this.extractDuoSize(duoItem);
    const topper = this.topperVariants[duoSize];
    if (!topper) return;

    const wrap = document.createElement('div');
    wrap.className = 'minicart__upsell';
    wrap.dataset.minicartUpsell = 'true';
    wrap.innerHTML = `
      <div class="minicart__upsell-label">Dobierz do Duo</div>
      <div class="minicart__upsell-title">Topper ${topper.size} pasujący do Twojego Duo</div>
      <p class="minicart__upsell-copy">Wyrównuje powierzchnię spania i zwiększa komfort. <strong>Dodany razem z Duo nie zwiększa kosztu dostawy.</strong></p>
      <div class="minicart__upsell-row">
        <div class="minicart__upsell-price">${this.formatMoney(topper.price)}</div>
        <button type="button" class="minicart__upsell-btn" data-action="add-topper" data-variant-id="${topper.id}">Dodaj topper</button>
      </div>
      <a class="minicart__upsell-link" href="/products/nakladka-na-materac-toper-tutus-duo?variant=${topper.id}">Zobacz szczegóły toppera</a>
      <div class="minicart__upsell-status" data-upsell-status></div>
    `;
    this.itemsContainer.insertAdjacentElement('afterend', wrap);
  }

  removeUpsell() {
    const existing = this.drawer?.querySelector('[data-minicart-upsell]');
    if (existing) existing.remove();
  }

  extractDuoSize(item) {
    if (Array.isArray(item.options_with_values)) {
      const sizeOption = item.options_with_values.find(option => option.name === 'Wymiary');
      if (sizeOption?.value) return sizeOption.value;
    }

    const title = item.variant_title || '';
    return Object.keys(this.topperVariants).find(size => title.includes(size)) || '';
  }

  getVariantTitle(item) {
    if (item.variant_title === 'Default Title' || !item.variant_title) return null;
    return item.variant_title;
  }

  renderTotal(totalPrice) {
    if (this.totalPrice) this.totalPrice.textContent = this.formatMoney(totalPrice);
  }

  async renderReview() {
    if (!this.reviewContainer) return;
    try {
      const response = await fetch('/?section_id=minicart-review', {
        method: 'GET',
        headers: { 'Accept': 'text/html' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('review_fetch_failed');
      const html = await response.text();
      this.reviewContainer.innerHTML = html;
    } catch (error) {
      this.reviewContainer.innerHTML = '';
      console.error('Minicart review error:', error);
    }
  }

  updateCartCount(count) {
    this.cartCountElements.forEach(el => {
      if (count > 0) {
        el.textContent = count;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  handleItemClick(e) {
    const target = e.target.closest('[data-action]');
    if (!target) return;

    const action = target.dataset.action;
    const key = target.dataset.key;

    if (action === 'increase') this.updateItemQuantity(key, 1);
    if (action === 'decrease') this.updateItemQuantity(key, -1);
    if (action === 'remove') this.removeItem(key);
    if (action === 'add-topper') this.addTopper(Number(target.dataset.variantId), target);
  }

  async addTopper(variantId, button) {
    if (this.isUpdating || !variantId) return;
    this.isUpdating = true;
    const status = button.closest('[data-minicart-upsell]')?.querySelector('[data-upsell-status]');
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Dodaję…';
    if (status) status.textContent = '';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('add_failed');
      const cart = await this.fetchCart();
      this.render(cart);
      this.updateCartCount(cart.item_count);
      this.dispatchCartUpdate(cart);
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      if (status) status.textContent = 'Nie udało się dodać toppera. Spróbuj ponownie.';
    } finally {
      this.isUpdating = false;
    }
  }

  async updateItemQuantity(key, change) {
    if (this.isUpdating) return;
    const itemEl = this.drawer.querySelector(`[data-item-key="${key}"]`);
    if (!itemEl) return;

    const quantityEl = itemEl.querySelector('.minicart__quantity-value');
    const currentQuantity = parseInt(quantityEl.textContent, 10);
    const newQuantity = currentQuantity + change;
    if (newQuantity < 1) return this.removeItem(key);

    this.isUpdating = true;
    itemEl.classList.add('is-updating');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQuantity })
      });
      const cart = await response.json();
      this.render(cart);
      this.updateCartCount(cart.item_count);
      this.dispatchCartUpdate(cart);
    } catch (error) {
      console.error('Update quantity error:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  async removeItem(key) {
    if (this.isUpdating) return;
    const itemEl = this.drawer.querySelector(`[data-item-key="${key}"]`);
    if (!itemEl) return;

    this.isUpdating = true;
    itemEl.classList.add('is-removing');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      const cart = await response.json();
      setTimeout(() => {
        this.render(cart);
        this.updateCartCount(cart.item_count);
        this.dispatchCartUpdate(cart);
      }, 300);
    } catch (error) {
      console.error('Remove item error:', error);
      itemEl.classList.remove('is-removing');
    } finally {
      this.isUpdating = false;
    }
  }

  dispatchCartUpdate(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `${amount} ZŁ`;
  }

  getSizedImageUrl(src, size) {
    if (!src) return '';
    return src.replace(/(\.[^.]+)$/, `_${size}$1`);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

window.openMinicart = function() {
  if (window.minicartInstance) window.minicartInstance.open();
};

window.refreshMinicart = function() {
  if (window.minicartInstance) window.minicartInstance.refresh();
};

document.addEventListener('DOMContentLoaded', () => {
  window.minicartInstance = new Minicart();
});
